import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import MDialog from '../../../components/controls/MDialog';
import RCBarChart from '../../../components/charts/recharts/rcBarChart';
import { formatDateTime } from '../../../components/ex_dayjs';
import type { IData } from '../../../types';

interface EventMappingChartProps {
    open: boolean;
    handleClose: () => void;
    data: IData[] | null;
    isLoading: boolean;
    filterList: IData;
}

/**
 * EventMappingChart - Dialog component displaying event mapping statistics as a bar chart
 * Shows how many events were mapped by each user
 */
const EventMappingChart = ({ open, handleClose, data, isLoading, filterList }: EventMappingChartProps) => {
    const { Mapped_Time_ge, Mapped_Time_le } = filterList;
    if (!data || data.length === 0) {
        return null;
    }

    /**
     * Transforms the array of user activity data into a structured array,
     * grouping by user and mapping market IDs to specific count fields.
     * * @param {Array<Object>} data - The source array of user data (user, marketid, count).
     * @returns {Array<Object>} The transformed array.
     */
    function transformData (data: IData[]): IData[] {
    // 1. Use reduce to build an intermediate object grouped by 'user'
        const groupedByUser = data.reduce((acc: IData, current: IData) => {
            const { user, marketid, count } = current;

            // Initialize the user object if it doesn't exist in the accumulator
            if (!acc[user]) {
                acc[user] = {
                    name: user,
                    vivid: 0,          // Corresponds to marketId = 1
                    ticketnetwork: 0,  // Corresponds to marketId = 2
                    gotickets: 0       // Corresponds to marketId = 13
                };
            }

            // 2. Assign the count to the corresponding field based on marketid
            switch (marketid) {
                case 1:
                // Accumulate count for marketId 1 (vivid)
                    acc[user].vivid += count;
                    break;
                case 2:
                // Accumulate count for marketId 2 (ticketnetwork)
                    acc[user].ticketnetwork += count;
                    break;
                case 13:
                // Accumulate count for marketId 13 (gotickets)
                    acc[user].gotickets += count;
                    break;
                default:
                // Ignore or handle unknown market IDs
                    break;
            }

            return acc;
        }, {}); // {} is the initial value (accumulator)

        // 3. Convert the object (groupedByUser) into an array of values
        const resultArray = Object.values(groupedByUser);

        return resultArray;
    }
    const chartData = transformData(data);
    // Calculate dynamic width based on number of users to prevent label overlap
    const chartWidth = chartData.length > 10 ? 2000 : 1500;

    return (
        <MDialog
            maxWidth='xl'
            open={open}
            handleClose={handleClose}
            name='event-mapping-chart'
            children={
                <Box sx={{ minHeight: '65vh', display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ marginBottom: '10px' }} variant="h4" textAlign={'center'}>Event Mapping Report</Typography>
                    <Typography sx={{ marginBottom: '20px' }} variant="subtitle1" textAlign={'center'}>
                        {Mapped_Time_ge
                            ? Mapped_Time_le
                                ? `From ${formatDateTime(Mapped_Time_ge)} to ${formatDateTime(Mapped_Time_le)}`
                                : `From ${formatDateTime(Mapped_Time_ge)} onwards`
                            : 'Mapping Time (from) Not Specified'}
                    </Typography>
                    {isLoading && <LinearProgress color='secondary' />}
                    {!isLoading && chartData.length === 0 && (
                        <div style={{ padding: '40px', fontSize: '16px', color: '#666' }}>
                            No mapping data found for the selected filters.
                        </div>
                    )}
                    {!isLoading && chartData.length > 0 && (
                        <RCBarChart
                            width={chartWidth}
                            data={chartData}
                            barSize={data.length > 18 ? 50 : ((data.length > 8) ? 70 : 90)}
                            xAxis={{ dataKey: 'name', label: { value: 'USER', dy: 20 } }}
                            yAxis={{ position: 'insideLeft', label: { value: 'Event Count', angle: -90, dx: -40 } }}
                            legend='hide'
                            showTotal={true}
                            seriesList={[
                                { dataKey: 'vivid', name: 'Vivid', fill: '#4caf50', stackId: 'a', labelList: { fill: 'white' } },
                                { dataKey: 'ticketnetwork', name: 'TicketNetwork', fill: '#9c27b0', stackId: 'a', labelList: { fill: 'white' } },
                                { dataKey: 'gotickets', name: 'GoTickets', fill: '#f16f65ff', stackId: 'a', labelList: { fill: 'white' } }

                            ]}
                        />
                    )}
                </Box>
            }
        />
    );
};

export default EventMappingChart;
